import { useEffect, useRef } from 'react'

import loadable from '@loadable/component'
import * as Sentry from '@sentry/react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { SlateKitProvider } from '@tribeplatform/react-slate-kit/components'
import { useSlateKit } from '@tribeplatform/react-slate-kit/hooks'

import { AppInitialProps } from './@types'
import { Wrapper } from './components/Admin/Editor/SlateWrappers'
import {
  CookieConsent,
  CookieContextProvider,
} from './components/Apps/CookieConsent'
import { AccountVerificationAlert } from './components/Auth/AccountVerificationAlert'
import { RenderError } from './components/Error/Error'
import { ErrorFallback } from './components/Error/ErrorBoundry'
import { SpaceRoutes } from './components/SpaceRoutes.component'
import { InteractionComponents } from './interactions/InteractionComponents'
import { MobileSidebarProvider } from './MobileSidebarProvider'
import { getRegisteredBlocks } from './registered-blocks'

function ScrollToTop() {
  const { pathname } = useLocation()
  const history = useHistory()
  const prevPathname = useRef(pathname)

  useEffect(() => {
    if (history.action !== 'POP' && pathname !== prevPathname.current) {
      window.scrollTo(0, 0)
    }
    prevPathname.current = pathname
  }, [history.action, pathname])

  return null
}

const Messaging = loadable(() => import('./components/Messaging'), {
  resolveComponent: components => components.Messaging,
})
const OAuth = loadable(() => import('./components/OAuth/OAuth'), {
  resolveComponent: module => module.OAuth,
})
const DesktopNotification = loadable(
  () => import('./components/DesktopNotification'),
  {
    resolveComponent: components => components.DesktopNotification,
  },
)

export const SlateApp = ({ pageProps }: { pageProps: AppInitialProps }) => {
  const registeredBlocks = useStaticIntl(getRegisteredBlocks)
  const slateKit = useSlateKit({
    blocks: registeredBlocks,
    blockWrapper: Wrapper,
  })

  const { data: { network, member } = pageProps.authToken } = useAuthToken({
    useQueryOptions: {
      // this is here to prevent the whole page from re-rendering
      notifyOnChangeProps: ['data'],
    },
  })

  return (
    <SlateKitProvider
      kit={slateKit}
      context={{
        network,
        actorId: member?.id,
        space: null,
        networkId: network?.id,
        memberBannerPlaceholders: [
          '/images/banners/banner-1.jpg',
          '/images/banners/banner-2.jpg',
          '/images/banners/banner-3.jpg',
          '/images/banners/banner-4.jpg',
          '/images/banners/banner-5.jpg',
        ],
      }}
      onError={({ error, ...extras }) => {
        const slateError = new RenderError({
          message: extras,
          error,
          name: 'SlateError',
        })
        logger.error(slateError)
        Sentry.withScope(scope => {
          scope.setTag('slate', 1)
          scope.setExtras(extras as Record<string, any>)
          Sentry.captureException(error)
        })
      }}
      ErrorFallback={ErrorFallback}
    >
      <MobileSidebarProvider navigationSlates={network?.navigationSlates}>
        <ScrollToTop />
        <InteractionComponents />
        <DesktopNotification />
        <AccountVerificationAlert />
        <Switch>
          <Route path="/messaging">
            <Messaging />
          </Route>
          <Route path="/oauth/authorize" exact>
            <OAuth />
          </Route>
          <Route path="/">
            <SpaceRoutes network={network} />
          </Route>
        </Switch>
        <CookieContextProvider>
          <CookieConsent />
        </CookieContextProvider>
      </MobileSidebarProvider>
    </SlateKitProvider>
  )
}
